import './Footer.scss';

import { type Country, Icon, isDefaultBrand, useConfig } from '@travelwin/core';
import { Trans, useTranslation } from 'react-i18next';

import { FeaturedCountry } from '@/services/resources/ResourceService';

import { useConsent } from '../../context/Consent';
import { useExternalHomepageConfig } from '../../hooks/useExternalHomepageConfig';
import { getBrandSpecificImage } from '../../utils';
import { isRemix } from '../../utils/ssrUtils';
import { Link } from '../Link';
import { AppStore, GooglePlayStore } from '../MobileStoreButton';
import { NavigationList } from './components';

/**
 * Currently Footer component is used in few places only. Hence, featuredCountries are accepted as property.
 * If it becomes used in more and more places and passing countries all over the places becomes too messy,
 * consider a different approach e.g. using in root loader.
 */
const Footer = ({
  featuredCountries,
  countries,
}: {
  featuredCountries?: FeaturedCountry[];
  countries?: Country[];
}) => {
  const className = 'c-Footer';
  const {
    WHITELABEL_BRAND,
    PRIVACY_POLICY_URL,
    TERMS_AND_CONDITIONS_URL,
    SHOW_STRIPE_CREDIT_CARD,
    BRAND_NAME,
    ACCESSIBILITY_STATEMENT_URL,
  } = useConfig();

  const { enabled: consentEnabled, renewCookiebot } = useConsent();
  const { getHomepageUrl, getLocalisedUrl } = useExternalHomepageConfig();
  const { t } = useTranslation();

  const getPaymentTitle = (provider: string) => {
    return t('payment_method.provider', '{{provider}} Payment Method', {
      provider,
    });
  };

  return (
    <div className={className}>
      <div className={`${className}__container`}>
        <div className={`${className}__top-container`}>
          <div className={`${className}__logo`}>
            <img
              src={getBrandSpecificImage('logoWhite', WHITELABEL_BRAND)}
              alt={t('logo.altText', '{{brandName}} Logo', {
                brandName: BRAND_NAME,
              })}
              width={128}
              height={64}
              loading="lazy"
            />
          </div>
          {isDefaultBrand(WHITELABEL_BRAND) && (
            <div className={`${className}__stores-buttons`}>
              <AppStore />
              <GooglePlayStore />
            </div>
          )}
        </div>

        <div className={`${className}__navigation-container`}>
          <NavigationList
            featuredCountries={featuredCountries}
            countries={countries}
          />
        </div>

        <div className={`${className}__payment-sns-container`}>
          <div className={`${className}__payments`}>
            {SHOW_STRIPE_CREDIT_CARD && (
              <>
                <Icon name="stripe" title={getPaymentTitle('Stripe')} />
                <Icon name="googlePay" title={getPaymentTitle('Google Pay')} />
                <Icon name="applePay" title={getPaymentTitle('Apple Pay')} />
                <Icon name="payPal" title={getPaymentTitle('Paypal')} />
              </>
            )}
          </div>

          {isDefaultBrand(WHITELABEL_BRAND) && (
            <div className={`${className}__sns`}>
              <a
                aria-label={t('footer.facebook', 'Facebook')}
                href={'https://www.facebook.com/simlocal/'}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Icon name="facebook" />
              </a>
              <a
                aria-label={t('footer.instagram', 'Instagram')}
                href={'https://www.instagram.com/simlocal/?hl=en-gb'}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Icon name="instagram" />
              </a>
              <a
                aria-label={t('footer.x', 'X')}
                href={'https://x.com/simlocal'}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Icon name="x" />
              </a>
              <a
                aria-label={t('footer.linkedIn', 'LinkedIn')}
                href={'https://www.linkedin.com/company/sim-local'}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Icon name="linkedIn" />
              </a>
              <a
                aria-label={t('footer.youtube', 'YouTube')}
                href={'https://www.youtube.com/@simlocal_esim'}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Icon name="youtube" />
              </a>
              <a
                aria-label={t('footer.tikTok', 'TikTok')}
                href={'https://www.tiktok.com/@simlocal_esim'}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Icon name="tikTok" />
              </a>
            </div>
          )}
        </div>

        <div className={`${className}__footer`}>
          <div className={`${className}__policy`}>
            <div className={`${className}__policy-links`}>
              <a
                className={`${className}__link`}
                href={getLocalisedUrl(PRIVACY_POLICY_URL)}
                data-testid="link-privacy-url"
                rel="noopener noreferrer"
              >
                {t('footer.privacy_policy', 'Privacy Policy')}
              </a>
              {isDefaultBrand(WHITELABEL_BRAND) &&
                (isRemix() ? (
                  <Link to="/cookie-policy" className={`${className}__link`}>
                    {t('footer.cookies_policy', 'Cookie Policy')}
                  </Link>
                ) : (
                  <a
                    className={`${className}__link`}
                    href={getHomepageUrl('/cookie-policy')}
                    rel="noopener noreferrer"
                  >
                    {t('footer.cookies_policy', 'Cookie Policy')}
                  </a>
                ))}
              {consentEnabled && (
                <a
                  className={`${className}__link`}
                  data-testid="link-cookie-policy"
                  onClick={renewCookiebot}
                >
                  {t('footer.change_consent', 'Manage Cookies')}
                </a>
              )}
              <a
                className={`${className}__link`}
                href={getLocalisedUrl(TERMS_AND_CONDITIONS_URL)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('footer.terms_and_conditions', 'Terms & Conditions')}
              </a>
              {ACCESSIBILITY_STATEMENT_URL && (
                <a
                  className={`${className}__link`}
                  href={
                    isDefaultBrand(WHITELABEL_BRAND)
                      ? getLocalisedUrl(ACCESSIBILITY_STATEMENT_URL)
                      : ACCESSIBILITY_STATEMENT_URL
                  }
                  data-testid="link-accessibility-statement"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t(
                    'footer.accessibility_statement',
                    'Accessibility Statement',
                  )}
                </a>
              )}
            </div>
            <p className={`${className}__all-rights-reserved`}>
              <Trans
                i18nKey="footer.text"
                defaults={
                  '© {{year}} Sim Local (IRELAND) Ltd. All Rights Reserved.'
                }
                values={{ year: new Date().getFullYear() }}
              />
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Footer;
